import type { BackendClient } from '../bridge/backend-client';
import type { TransportInfo } from '../../workers/backend.worker';
import { t } from '@vitesse/base-ui/i18n';

export type { TransportInfo };

/**
 * The connection-badge label: the data-plane path ("LAN" / "Private server")
 * plus its latency, e.g. "Private server · 183ms". `info.rttMs` is now a REAL
 * periodic network RTT — the worker re-probes the api-gateway's pre-auth
 * /.well-known endpoint every few seconds over the WARM HTTP/2 connection (one
 * round-trip ≈ the user's ping), so the badge tracks live latency and refreshes
 * on its own. The latency is shown ONLY when PLAUSIBLE (≤5s); a larger value is a
 * network blip, not latency, so we fall back to the path alone rather than a
 * bogus "312008ms". Formatted ms vs s for readability.
 */
export function connViaLabel(info: TransportInfo): string {
  const via = info.path === 'lan' ? t('gateway.viaLan') : t('gateway.viaCloud');
  const rtt = info.rttMs;
  if (rtt > 0 && rtt <= 5000) {
    return `${via} · ${rtt < 1000 ? `${rtt}ms` : `${(rtt / 1000).toFixed(1)}s`}`;
  }
  return via;
}

/*
 * TransportManager — main-thread PROXY of the Backend Worker's data-plane
 * transport selection FOR ONE GATEWAY. The worker races that gateway's LAN edge
 * api-gateway vs the cloud and serves over whichever it prefers; this manager
 * subscribes to that gateway's status (path + RTT), holds the latest, and emits
 * `change` so the gateway card + base-page re-render their "Connected via LAN ·
 * 12ms" indicator. One instance per gateway (each gateway races independently).
 */
export class TransportManager extends EventTarget {
  private info: TransportInfo = { path: 'cloud', rttMs: 0 };
  private port?: MessagePort;

  constructor(
    private readonly backend: BackendClient,
    private readonly gatewayId: string,
  ) {
    super();
  }

  /** The path the data plane chose ('lan' | 'cloud') + the probe RTT in ms. */
  get transportInfo(): TransportInfo {
    return this.info;
  }

  async start(): Promise<void> {
    this.port = await this.backend.api.subscribeTransportStatus(this.gatewayId);
    this.port.onmessage = (e: MessageEvent<TransportInfo>) => {
      this.info = e.data;
      this.dispatchEvent(new CustomEvent('change'));
    };
    this.port.start();
  }

  stop(): void {
    // Tell the worker to drop this listener from the gateway's fan-out BEFORE
    // closing — closing the port alone doesn't notify the worker side, so its
    // port1 would linger in `listeners` and keep receiving RTT-probe postMessages.
    this.port?.postMessage({ kind: 'close' });
    this.port?.close();
    this.port = undefined;
  }
}
